import React, { useState } from 'react';
import { Collapse, Row, Col, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, UncontrolledTooltip, Input } from 'reactstrap';
import __ from 'utils/i18n';
import moment from 'moment';
import CardIcon from 'components/CardIcon';
import Loader from 'components/Loader';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { updateSubscription } from '../../_shared/resources';
import { useSelector } from 'react-redux';
import '../../../styles.scss';
import { notification } from 'utils/notifications';
import { RECURRING_PAYMENTS_SEARCH_MAX_LENGTH, recurringPaymentsSearchFilter, isValidIdRef } from '../../../constants';

const getLastChargeByCreatedAt = (charges) => {
  if (!charges?.length) return null;
  return charges.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
};

export const SubscriberDetailPanel = ({ subscriber, charges, loadingCharges, onRequestCardUpdate, onPauseSubscription, onCancelSubscription, onReactivateSubscription, onSubscriberUpdated }) => {
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [isEditingIdRef, setIsEditingIdRef] = useState(false);
  const [idRefValue, setIdRefValue] = useState(subscriber?.externalReference || '');
  const [isSavingIdRef, setIsSavingIdRef] = useState(false);
  const { token } = useSelector((state) => state.api);

  if (!subscriber) return null;

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).format('DD/MM/YYYY');
  };

  const getStatusBadgeClass = () => {
    const statusClasses = {
      'Active': 'success',
      'Trial': 'success',
      'Cancelled': 'danger',
      'Paused': 'warning',
      'Expired': 'secondary',
      'PastDue': 'information',
    };
    return statusClasses[subscriber.status] || 'secondary';
  };

  const getStatusText = () => {
    const statusTexts = {
      'Active': __('dashboard.sections.recurringPayments.subscribers.table.statuses.active'),
      'Trial': __('dashboard.sections.recurringPayments.subscribers.table.statuses.trial'),
      'Cancelled': __('dashboard.sections.recurringPayments.subscribers.table.statuses.cancelled'),
      'Paused': __('dashboard.sections.recurringPayments.subscribers.table.statuses.paused'),
      'Expired': __('dashboard.sections.recurringPayments.subscribers.table.statuses.expired'),
      'PastDue': __('dashboard.sections.recurringPayments.subscribers.table.statuses.pastDue'),
    };
    return statusTexts[subscriber.status] || subscriber.status;
  };

  const formatFrequency = (frequency) => {
    if (!frequency) return '';
    
    // Normalizar la frecuencia a PascalCase (primera letra mayúscula)
    const normalizedFrequency = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();
    
    const frequencies = {
      'Daily': __('dashboard.sections.recurringPayments.plans.table.frequencies.daily'),
      'Weekly': __('dashboard.sections.recurringPayments.plans.table.frequencies.weekly'),
      'Monthly': __('dashboard.sections.recurringPayments.plans.table.frequencies.monthly'),
      'Quarterly': __('dashboard.sections.recurringPayments.plans.table.frequencies.quarterly'),
      'Yearly': __('dashboard.sections.recurringPayments.plans.table.frequencies.yearly'),
    };
    return frequencies[normalizedFrequency] || frequency;
  };

  const getChargeStatusBadgeClass = (status) => {
    const statusClasses = {
      'succeeded': 'success',
      'pending': 'warning',
      'processing': 'warning',
      'failed': 'danger',
      'cancelled': 'danger',
      'pastdue': 'warning',
      'skippedzeroamount': 'secondary',
      'skippednullamount': 'danger',
    };
    return statusClasses[status] || 'secondary';
  };

  const getChargeStatusText = (status) => {
    const statusTexts = {
      'succeeded': __('dashboard.sections.recurringPayments.subscribers.detail.chargeStatus.received'),
      'pending': __('dashboard.sections.recurringPayments.charges.table.statuses.pending'),
      'processing': __('dashboard.sections.recurringPayments.charges.table.statuses.processing'),
      'failed': __('dashboard.sections.recurringPayments.subscribers.detail.chargeStatus.rejected'),
      'cancelled': __('dashboard.sections.recurringPayments.subscribers.detail.chargeStatus.rejected'),
      'pastdue': __('dashboard.sections.recurringPayments.charges.table.statuses.pastdue'),
      'skippedzeroamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippedzeroamount'),
      'skippednullamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippednullamount'),
    };
    return statusTexts[status] || status;
  };

  const handleSaveIdRef = async () => {
    if (!subscriber || !token) return;
    if (idRefValue && !isValidIdRef(idRefValue)) {
      notification.error(__('dashboard.sections.recurringPayments.subscribers.detail.idRefInvalid'));
      return;
    }

    setIsSavingIdRef(true);
    try {
      await fetchResourceRP(() => updateSubscription(
        subscriber.subscriptionId,
        { external_reference: idRefValue || null },
        token
      ));
      
      setIsEditingIdRef(false);
      notification.success(__('dashboard.sections.recurringPayments.subscribers.detail.idRefUpdated'));
      
      // Actualizar el subscriber localmente
      if (onSubscriberUpdated) {
        onSubscriberUpdated({
          ...subscriber,
          externalReference: idRefValue || null,
        });
      }
    } catch (error) {
      console.error('Error updating external reference:', error);
      notification.error(__('dashboard.sections.recurringPayments.subscribers.detail.errorUpdatingIdRef'));
      // Revertir el valor en caso de error
      setIdRefValue(subscriber?.externalReference || '');
    } finally {
      setIsSavingIdRef(false);
    }
  };

  const handleCancelEditIdRef = () => {
    setIdRefValue(subscriber?.externalReference || '');
    setIsEditingIdRef(false);
  };

  const lastCharge = getLastChargeByCreatedAt(charges);

  return (
    <div className="panel-flex">
      <div>
        <div className="recurring-payments-detail-container">
          <div className="text-center mb-4">
            <div className="d-flex justify-content-center align-items-center mb-2 text-wrap">
              <h3 className="mb-0 font-weight-bold text-wrap">{subscriber.customer?.fullName || '-'}</h3>
              
              {/* Si es cancelada, no mostrar nada */}
              {subscriber.status === 'Cancelled' ? null : (
                /* Para suscripciones activas, trial, pastDue y pausadas, mostrar dropdown */
                <UncontrolledDropdown className="ml-2">
                  <DropdownToggle tag="button" className="btn btn-link text-dark p-0">
                    <i className="mdi mdi-dots-vertical mdi-24px"></i>
                  </DropdownToggle>
                  <DropdownMenu right className="recurring-payments-dropdown-menu">
                    <DropdownItem onClick={() => onRequestCardUpdate && onRequestCardUpdate(subscriber)}>
                      {__('dashboard.sections.recurringPayments.subscribers.actions.requestCardUpdate')}
                    </DropdownItem>
                    <DropdownItem divider />
                    {(subscriber.status === 'Active' || subscriber.status === 'Trial') && (
                      <>
                        <DropdownItem onClick={() => onPauseSubscription && onPauseSubscription(subscriber)}>
                          {__('dashboard.sections.recurringPayments.subscribers.actions.pauseSubscription')}
                        </DropdownItem>
                      </>
                    )}
                    <DropdownItem className="text-danger" onClick={() => onCancelSubscription && onCancelSubscription(subscriber)}>
                      {__('dashboard.sections.recurringPayments.subscribers.actions.cancelSubscription')}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>
            
            <p className="text-muted mb-1 text-wrap">{subscriber.customer?.email || '-'}</p>
            {subscriber.customer?.phone && (
              <p className="text-muted mb-1">{subscriber.customer.phone}</p>
            )}
            <div className="mb-3 d-flex justify-content-center">
              {isEditingIdRef ? (
                <div className="d-flex flex-column align-items-center">
                  <div className="d-flex align-items-center justify-content-center">
                    <Input
                      type="text"
                      value={idRefValue}
                      onChange={(e) => setIdRefValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isSavingIdRef) {
                          handleSaveIdRef();
                        } else if (e.key === 'Escape' && !isSavingIdRef) {
                          handleCancelEditIdRef();
                        } else if (
                          e.key.length === 1 &&
                          !recurringPaymentsSearchFilter(e.key) &&
                          !e.ctrlKey &&
                          !e.metaKey &&
                          !e.altKey
                        ) {
                          e.preventDefault();
                        } else if (
                          e.key.length === 1 &&
                          idRefValue.length >= RECURRING_PAYMENTS_SEARCH_MAX_LENGTH &&
                          !e.ctrlKey &&
                          !e.metaKey &&
                          !e.altKey &&
                          e.key !== 'Backspace' &&
                          e.key !== 'Delete'
                        ) {
                          e.preventDefault();
                        }
                      }}
                      disabled={isSavingIdRef}
                      autoFocus
                      style={{ maxWidth: '200px', display: 'inline-block', marginRight: '8px' }}
                      placeholder={isSavingIdRef ? __('dashboard.sections.recurringPayments.subscribers.detail.updatingIdRef') || 'Actualizando...' : ''}
                    />
                    <div className="d-flex align-items-center">
                    {isSavingIdRef ? (
                      <span className="text-muted mr-2" style={{ fontSize: '0.875rem' }}>
                        {__('dashboard.sections.recurringPayments.subscribers.detail.updatingIdRef') || 'Actualizando...'}
                      </span>
                    ) : (
                      <>
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleSaveIdRef}
                          disabled={isSavingIdRef || (idRefValue && !isValidIdRef(idRefValue))}
                          className="mr-1"
                          style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <i className="mdi mdi-check"></i>
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={handleCancelEditIdRef}
                          disabled={isSavingIdRef}
                          style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <i className="mdi mdi-close"></i>
                        </Button>
                      </>
                    )}
                    </div>
                  </div>
                  {isEditingIdRef && !isSavingIdRef && idRefValue && !isValidIdRef(idRefValue) && (
                    <small className="text-danger d-block mt-1 text-center">
                      {__('dashboard.sections.recurringPayments.subscribers.detail.idRefInvalid')}
                    </small>
                  )}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center">
                    <p className="text-muted mb-0 text-wrap">
                    <strong>{__('dashboard.sections.recurringPayments.subscribers.detail.idRef')}:</strong> {subscriber.externalReference || '-'}
                  </p>
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => {
                      setIdRefValue(subscriber?.externalReference || '');
                      setIsEditingIdRef(true);
                    }}
                    className="ml-2 p-0"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <i className="mdi mdi-pencil"></i>
                  </Button>
                </div>
              )}
            </div>
            
            <div>
              <span className={`badge badge-${getStatusBadgeClass()}`}>
                {getStatusText()}
              </span>
            </div>
          </div>

          {/* Próximo cobro y Cobro anterior */}
          {subscriber.nextBillingDate && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted text-wrap">
                  {__('dashboard.sections.recurringPayments.subscribers.detail.nextCharge')} ({formatDate(subscriber.nextBillingDate)})
                </span>
                {(subscriber.plan?.pricingType === 'dynamic' || subscriber.plan?.pricingType === 'Variable' || subscriber.plan?.pricing_type === 'dynamic') ? (
                  // Plan de monto variable: usar nextAmount de la subscription
                  subscriber.nextAmount !== null && subscriber.nextAmount !== undefined ? (
                    <div className="d-flex align-items-center">
                      <span className="text-muted mr-2" style={{ fontSize: '0.875rem' }}>
                        {__('dashboard.sections.recurringPayments.subscribers.detail.updated')}
                      </span>
                      <span className="font-weight-medium text-wrap">
                        {formatCurrency(subscriber.nextAmount, subscriber.plan?.currency)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                      {__('dashboard.sections.recurringPayments.subscribers.detail.notUpdated')}
                    </span>
                  )
                ) : (
                  // Plan de monto fijo: siempre mostrar el monto del plan
                  subscriber.plan?.amount && (
                      <span className="font-weight-medium text-wrap">
                      {formatCurrency(subscriber.plan.amount, subscriber.plan.currency)}
                    </span>
                  )
                )}
              </div>
            </div>
          )}
          
          {lastCharge && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">
                  {__('dashboard.sections.recurringPayments.subscribers.detail.lastCharge')} ({formatDate(lastCharge.updatedAt)})
                </span>
                {subscriber.lastAmount !== null && subscriber.lastAmount !== undefined && (
                  <span className="font-weight-medium text-wrap-amount">
                    {formatCurrency(subscriber.lastAmount, subscriber.plan?.currency)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Plan con fondo blanco dentro del contenedor gris */}
          {subscriber.plan && (
            <div className="recurring-payments-detail-block">
              <div className="d-flex justify-content-between align-items-center">
                <div className="font-weight-bold text-wrap">{subscriber.plan.name}</div>
                {subscriber.plan.amount && (
                  <div className="font-weight-bold text-wrap-amount">
                    {formatCurrency(subscriber.plan.amount, subscriber.plan.currency)}
                    {subscriber.plan.billingFrequency && (
                      <span className="text-muted">/{formatFrequency(subscriber.plan.billingFrequency)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <div 
              onClick={() => setShowMoreDetails(!showMoreDetails)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
            >
              <h6 className="mb-0">
                {__('dashboard.sections.recurringPayments.subscribers.detail.viewMoreDetails')}
              </h6>
              <i className={`mdi mdi-chevron-${showMoreDetails ? 'up' : 'down'} mdi-24px text-muted`}></i>
            </div>

            <Collapse isOpen={showMoreDetails}>
              <div className="mt-3">
                <Row>
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.subscribers.detail.subscriptionDate')}
                    </small>
                    <span>{formatDate(subscriber.createdAt)}</span>
                  </Col>
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.subscribers.detail.billingDay')}
                    </small>
                    <span>{formatDate(subscriber.nextBillingDate)}</span>
                  </Col>
                  {subscriber.trialEndDate && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.subscribers.detail.trialEndDate')}
                      </small>
                      <span>{formatDate(subscriber.trialEndDate)}</span>
                    </Col>
                  )}
                  {subscriber.endDate && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.subscribers.detail.endDate')}
                      </small>
                      <span>{formatDate(subscriber.endDate)}</span>
                    </Col>
                  )}
                  {subscriber.billingCyclesCompleted !== undefined && subscriber.plan?.billingCycles && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.subscribers.detail.billingCycles')}
                      </small>
                      <span>{subscriber.billingCyclesCompleted}/{subscriber.plan.billingCycles}</span>
                    </Col>
                  )}
                </Row>
              </div>
            </Collapse>
          </div>
        </div>

        <div className="recurring-payments-section mt-4">
          {/* Encabezado con fondo gris */}
          <div className="recurring-payments-section-header d-flex justify-content-between align-items-center p-3 mb-0">
            <h6 className="mb-0 font-weight-bold">
              {__('dashboard.sections.recurringPayments.subscribers.detail.charges')}
              {charges && charges.length > 0 && ` (${charges.length})`}
            </h6>
          </div>
          
          {/* Contenido de cobros */}
          {loadingCharges ? (
            <div className="py-4">
              <Loader />
            </div>
          ) : charges && charges.length > 0 ? (
            <div className="pt-2">
              {charges.map((charge, index) => {
                const isRejected = charge.status === 'failed' || charge.status === 'cancelled';
                const isSkipped = charge.status === 'skippedzeroamount' || charge.status === 'skippednullamount';
                const tooltipId = `subscriber-charge-tooltip-${charge.chargeId || index}`;
                
                return (
                  <div 
                    key={charge.chargeId || index} 
                    className="recurring-payments-list-item d-flex justify-content-between align-items-center py-3 px-2"
                  >
                    <div className="d-flex align-items-center">
                      <CardIcon src={{ issuerName: charge.paymentMethodBrand }} />
                      <div className="ml-3">
                        <div className="font-weight-bold">
                          **** {charge.paymentMethodLast4 || '-'}
                        </div>
                        <div className="text-muted small">{formatDate(charge.updatedAt)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-weight-bold mb-1">{formatCurrency(charge.amount, charge.currency)}</div>
                      <span className={`badge badge-${getChargeStatusBadgeClass(charge.status)} recurring-payments-badge-small`}>
                        {getChargeStatusText(charge.status)}
                        {(isRejected || isSkipped) && (
                          <>
                            <i 
                              id={tooltipId}
                              className="mdi mdi-information-outline ml-1"
                              style={{ fontSize: '12px', cursor: 'pointer' }}
                            ></i>
                            <UncontrolledTooltip placement="top" target={tooltipId}>
                              {isSkipped 
                                ? __(`dashboard.sections.recurringPayments.charges.table.tooltips.${charge.status}`)
                                : charge.failureReason
                              }
                            </UncontrolledTooltip>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <i className="mdi mdi-credit-card-outline mdi-48px mb-2 d-block"></i>
              <p>{__('dashboard.sections.recurringPayments.subscribers.detail.noCharges')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Botón Reactivar suscripción solo para suscripciones pausadas */}
      {subscriber.status === 'Paused' && (
        <div className="panel-flex-footer">
          <Button 
            color="primary" 
            block
            className="font-weight-bold d-flex align-items-center justify-content-center"
            style={{ borderRadius: '8px' }}
            onClick={() => onReactivateSubscription && onReactivateSubscription(subscriber)}
          >
            <i className="mdi mdi-refresh mr-2"></i>
            {__('dashboard.sections.recurringPayments.subscribers.actions.reactivateSubscription')}
          </Button>
        </div>
      )}

      {/* Botón Solicitar actualización de tarjeta solo para suscripciones en intento de cobro (PastDue) */}
      {subscriber.status === 'PastDue' && (
        <div className="panel-flex-footer">
          <Button 
            color="primary" 
            block
            className="font-weight-bold d-flex align-items-center justify-content-center"
            style={{ borderRadius: '8px' }}
            onClick={() => onRequestCardUpdate && onRequestCardUpdate(subscriber)}
          >
            <i className="mdi mdi-credit-card mr-2"></i>
            {__('dashboard.sections.recurringPayments.subscribers.actions.requestCardUpdate')}
          </Button>
        </div>
      )}
    </div>
  );
};

